//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';
import { ILogService, IQService, IWindowService } from 'angular';
import $ from 'jquery';
import merge from 'lodash/fp/merge';
import once from 'lodash/fp/once';

import '@legacy-components/mobile-install-redirect/mobile-install-redirect';
import 'app/freshdesk-login/freshdesk-login.app';

import {
  routingRules as accountSettingsRoutingRules,
  states as accountSettingsStates,
} from '@account-settings/settings.routes';
import AuthType from '@apis/auth/models/AuthType';
import { runOauthForProvider } from '@apis/auth/oauthForProviderApi';
import {
  routingRules as campaignsRoutingRules,
  states as campaignsStates,
} from '@campaigns/campaigns.routes';
import {
  routingRules as commerceRoutingRules,
  states as commerceStates,
} from '@commerce/commerce.routes';
import {
  routingRules as creatorCommunityRoutingRules,
  states as creatorCommunityStates,
} from '@creator-community/creator-community.routes';
import {
  routingRules as exportRoutingRules,
  states as exportStates,
} from '@export/export.routes';
import {
  routingRules as facebookRoutingRules,
  states as facebookStates,
} from '@facebook/facebook.routes';
import {
  routingRules as galleriesRoutingRules,
  states as galleriesStates,
} from '@galleries/galleries.routes';
import {
  states as influencerStates,
  routingRules as influencersRoutingRules,
} from '@influencers/influencers.routes';
import {
  routingRules as customReportingRoutingRules,
  states as customReportingStates,
} from '@insights/custom-reporting.routes';
import {
  routingRules as insightsRoutingRules,
  states as insightsStates,
} from '@insights/insights.routes';
import {
  routingRules as instagramAccountRoutingRules,
  states as instagramAccountStates,
} from '@instagram-account/instagram-account-routing';
import {
  routingRules as listeningRoutingRules,
  states as listeningStates,
} from '@listening/listening.routes';
import {
  routingRules as pinterestRoutingRules,
  states as pinterestStates,
} from '@pinterest/pinterest.routes';
import {
  routingRules as samplingRoutingRules,
  states as samplingStates,
} from '@sampling/sampling.routes';
import { states as socialResponseStates } from '@social-response/socialResponse.routes';
import {
  routingRules as taggingRoutingRules,
  states as taggingStates,
} from '@tagging/tagging.routes';
import {
  routingRules as tiktokRoutingRules,
  states as tiktokStates,
} from '@tiktok/tiktok.routes';
import {
  routingRules as twitterRoutingRules,
  states as twitterStates,
} from '@twitter/twitter.routes';
import {
  routingRules as twitterV2RoutingRules,
  states as twitterV2States,
} from '@twitterV2/twitterV2.routes';
import { states as scheduleStates } from '@universal-scheduling/schedule.routes';
import Transition from '@utils/Transition';
import {
  routingRules as youtubeRoutingRules,
  states as youtubeStates,
} from '@youtube/youtube.routes';

import {
  routingRules as automatedReportsRoutingRules,
  states as automatedReportsStates,
} from 'app/automated-reports/automated-reports.routes';
import {
  routingRules as bookmarkletRoutingRules,
  states as bookmarkletStates,
} from 'app/bookmarklet/bookmarklet.routes';
import {
  routingRules as conversationsRoutingRules,
  states as conversationsStates,
} from 'app/conversations/conversations.routes';
import { crl8, isAuthedSession } from 'app/curalate.globals';
import { states as emailExportStates } from 'app/email-exports/email-export.routes';
import { routingRules as fanreelRoutingRules } from 'app/fanreel/fanreel.routes';
import {
  INamedState,
  NavGroup,
  StateFactory,
  componentNameToTemplate,
  hasAllFeatures,
  hasFeature,
  hasSomeFeatures,
  isInDevMode,
  lazyLoadModule,
} from 'app/initialization/routing/stateHelpers';
import {
  addDashboardParentToStates,
  addNamesToStates,
} from 'app/initialization/routing/topLevelStateUtils';
import {
  routingRules as libraryRoutingRules,
  states as libraryStates,
} from 'app/library/library.routes';
import {
  routingRules as oauthRoutingRules,
  states as oauthStates,
} from 'app/oauth/oauth.routes';
import {
  routingRules as shareRoutingRules,
  states as shareStates,
} from 'app/share/share.routes';
import {
  routingRules as syndicationRoutingRules,
  states as syndicationStates,
} from 'app/syndication/syndication.routes';

const CLIENT_ID_PARAM = 'clientId';
const BV_SOURCE_PARAM = 'bvSource';

const welcomeState = StateFactory({
  url: '/welcome',
  template: componentNameToTemplate('welcomeScreen'),
  redirectTo: () => {
    const isBazaarvoiceSyndicationEnabled = hasFeature(
      FeatureNames.BazaarvoiceNetworkSyndicationFrontend,
    );

    if (crl8.session?.client.isShareV2Vendor) {
      if (isBazaarvoiceSyndicationEnabled) {
        return dashboardStatesByName.galleries.name;
      } else {
        return dashboardStatesByName.share.name;
      }
    }

    return undefined;
  },
  data: {
    title: 'Welcome',
    navGroup: NavGroup.HOME,
  },
});

const routingRules = [
  automatedReportsRoutingRules,
  bookmarkletRoutingRules,
  instagramAccountRoutingRules,
  listeningRoutingRules,
  conversationsRoutingRules,
  facebookRoutingRules,
  fanreelRoutingRules,
  libraryRoutingRules,
  syndicationRoutingRules,
  oauthRoutingRules,
  taggingRoutingRules,
  influencersRoutingRules,
  twitterRoutingRules,
  twitterV2RoutingRules,
  accountSettingsRoutingRules,
  galleriesRoutingRules,
  exportRoutingRules,
  shareRoutingRules,
  insightsRoutingRules,
  customReportingRoutingRules,
  pinterestRoutingRules,
  samplingRoutingRules,
  tiktokRoutingRules,
  campaignsRoutingRules,
  creatorCommunityRoutingRules,
  commerceRoutingRules,
  youtubeRoutingRules,
];

const topLevelStateConfigs = {
  dashboard: {
    abstract: true,
    // No componentnNameToTemplate, uses different css
    template: '<dashboard-page></dashboard-page>',
  },

  login: StateFactory({
    url: '/login',
    template: '<curalate-login></curalate-login>',
    data: {
      allowLoggedOut: true,
      title: 'Login',
      navGroup: NavGroup.HOME,
      hasAccess: () => !crl8.isAuthedSession(),
    },
    resolve: {
      ...lazyLoadModule('curalate.login', () => import('./login/login.app')),
    },
  }),

  'freshdesk-login': StateFactory({
    url: '/freshdesk-login?host_url',
    template: componentNameToTemplate('freshdeskLoginPage'),
    data: {
      title: 'Freshdesk',
      navGroup: NavGroup.HOME,
    },
  }),

  'mobile-install': StateFactory({
    url: '/mobile-install',
    template: componentNameToTemplate('mobileInstallRedirect'),
    data: {
      allowLoggedOut: true,
      title: 'Mobile Install Redirect',
      navGroup: NavGroup.HOME,
    },
  }),

  bvRedirectLogin: StateFactory({
    url: `/login-from-bv-portal?:${CLIENT_ID_PARAM}`,
    template: '',
    redirectTo: (transition: Transition) => {
      const { [CLIENT_ID_PARAM]: clientId } = transition.params('to') as Partial<
        Record<string, string>
      >;

      // Preserve only allowed query parameters (bvSource)
      const existingUrlParams = new URLSearchParams(window.location.search);
      const newQueryParams = new URLSearchParams();

      // Only preserve bvSource parameter
      const bvSource = existingUrlParams.get(BV_SOURCE_PARAM);
      if (bvSource) {
        newQueryParams.set(BV_SOURCE_PARAM, bvSource);
      }

      // Include clientId if provided
      if (clientId) {
        newQueryParams.set(CLIENT_ID_PARAM, clientId);
      }

      const queryString = newQueryParams.toString();
      const redirectPath = queryString ? `/?${queryString}` : '/';

      // If user is logged in, redirect to welcome screen with query params
      const session = crl8.session;
      if (isAuthedSession(session)) {
        window.location.href = redirectPath;
        return false; // Prevent default redirect
      }

      // User is logged out - redirect to Cognito
      const stateParam = {
        loginRedirect: redirectPath,
      };

      runOauthForProvider(AuthType.BVOkta, stateParam);
    },
    data: {
      title: 'BV Login',
      navGroup: NavGroup.HOME,
      allowLoggedOut: true,
    },
  }),

  ...bookmarkletStates,
};

// All states which are a part of the logged-in dashboard experience with the default topnav
const dashboardStateConfigs = {
  index: merge(welcomeState, { url: '/' }),

  error: StateFactory({
    url: '/error',
    template: componentNameToTemplate('errorPage'),
    data: {
      title: 'Page Not Found',
      navGroup: NavGroup.HOME,
      hasAccess: () => true,
    },
    resolve: {
      ...lazyLoadModule('curalate.error', () => import('./error/error.app')),
    },
  }),

  ...instagramAccountStates,

  integrationSettings: StateFactory({
    url: '/integration-settings',
    template: '',
    redirectTo: (transition: Transition) => {
      if (!transition.$to().data.hasAccess()) {
        return defaultState.name;
      }

      return 'account-settings.ecommerceSetup';
    },
    data: {
      title: 'Integration Setup',
      navGroup: NavGroup.GALLERIES,
      hasAccess: () =>
        hasSomeFeatures(
          'ecommerce_integrations_magento',
          'ecommerce_integrations_shopify',
        ),
    },
  }),

  fanreelSetup: StateFactory({
    url: '/integration-settings/fanreel-setup',
    template: componentNameToTemplate('fanreelEcommIntegrationSummary'),
    data: {
      title: 'Fanreel Setup',
      navGroup: NavGroup.GALLERIES,
      hasAccess: () =>
        hasAllFeatures('ecommerce_fanreel_integration') && isInDevMode(),
    },
    resolve: {
      ...lazyLoadModule(
        'curalate',
        () =>
          import(
            '@legacy-components/fanreel-ecomm-integration/fanreel-ecomm-integration-summary'
          ),
      ),
      siteJs: [
        '$log',
        '$q',
        '$window',
        once(($log: ILogService, $q: IQService, $window: IWindowService) =>
          $q((resolve) => {
            $.getScript(
              'https://cdn.curalate.com/sites/default/site/latest/site.min.js',
            )
              .then(
                () =>
                  $window.crl8.EventBus.dispatchEvent('siteReady', {
                    name: 'default',
                  }),
                (error) =>
                  $log.error(
                    `site.min.js returned with status code ${error.status}`,
                  ),
              )
              .then(resolve, resolve); // Swallow the failure here so that the page will at least load
          }),
        ),
      ],
    },
  }),

  onboarding: StateFactory({
    url: '/onboarding',
    template: componentNameToTemplate('onboardingScreenContainer'),
    data: {
      title: 'Welcome',
      navGroup: NavGroup.HOME,
    },
  }),

  ...libraryStates,

  ...facebookStates,

  ...influencerStates,

  ...conversationsStates,

  ...taggingStates,

  ...pinterestStates,

  ...syndicationStates,

  ...oauthStates,

  ...emailExportStates,

  ...automatedReportsStates,

  ...twitterStates,

  ...twitterV2States,

  ...accountSettingsStates,

  ...galleriesStates,

  ...exportStates,

  ...shareStates,

  ...insightsStates,

  ...customReportingStates,

  ...pinterestStates,

  ...samplingStates,

  ...tiktokStates,

  ...scheduleStates,

  ...listeningStates,

  ...socialResponseStates,

  ...campaignsStates,

  ...creatorCommunityStates,

  ...commerceStates,

  ...youtubeStates,

  welcome: welcomeState,
};

export const dashboardStatesByName = addDashboardParentToStates(
  addNamesToStates(dashboardStateConfigs),
);

const topLevelStates = addNamesToStates(topLevelStateConfigs);

export const webappStates: INamedState[] = (
  Object.values(dashboardStatesByName) as INamedState[]
).concat(Object.values(topLevelStates));

export const topLevelRoutingRules = routingRules;

export const defaultState = dashboardStatesByName.index;
export const syndicationDefaultState =
  dashboardStatesByName['syndication.summary'];
export const loggedOutDefaultState = topLevelStates.login;
type TopLevelStateNames = keyof typeof topLevelStates;
export type DashboardStateNames = keyof typeof dashboardStatesByName;

/**
 * Routable state names for Angular router.
 * - Absolute paths
 * - "^": Parent state
 * - ".": Current state
 * @type {string} RoutableStateName
 */
export type RoutableStateName =
  | TopLevelStateNames
  | DashboardStateNames
  | '^'
  | '.';
